/**
 * @fileoverview Filter Module - Configurable filtering component
 * 
 * This module provides a reusable filtering interface that can be configured
 * for different contexts (admin users, admin parrots, user dashboard, conversations).
 * 
 * Requirements: 3.1, 3.2, 10.1, 10.2
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useModuleContext, useModuleEvents } from '../context/ModuleContextProvider.jsx';
import { ModuleEventType } from '../types/index.js';
import { 
  FilterConfig, 
  validateFilterConfig, 
  getDefaultFilterConfig,
  createActiveFilter,
  applyFilters,
  CONTEXT_FILTER_DEFINITIONS 
} from './types.js';
import { FilterInterface } from './components/FilterInterface.jsx';
import { FilterPresets } from './components/FilterPresets.jsx';
import { FilterSummary } from './components/FilterSummary.jsx';
import { useFilterPersistence } from './hooks/useFilterPersistence.js';
import { useFilterValidation } from './hooks/useFilterValidation.js';
import { useFilterDebounce } from './hooks/useFilterDebounce.js';

/**
 * Filter Module Component
 * 
 * @param {Object} props - Component props
 * @param {FilterConfig} props.config - Module configuration
 * @param {ModuleContext} props.context - Module context
 * @param {function(ModuleEvent): void} [props.onEvent] - Event handler
 * @param {string} [props.className] - CSS class name
 * @param {string} [props.testId] - Test ID
 */
const FilterModule = ({ 
  config, 
  context, 
  onEvent, 
  className = '', 
  testId = 'filter-module' 
}) => {
  // Validate configuration
  const configValidation = validateFilterConfig(config);
  if (!configValidation.valid) {
    throw new Error(`Invalid Filter configuration: ${configValidation.errors.join(', ')}`);
  }

  // Module context and events
  const moduleContext = useModuleContext();
  const { emit: emitEvent } = useModuleEvents('*', null, { enabled: false });

  // Module state
  const [moduleState, setModuleState] = useState({
    initialized: false,
    loading: false,
    error: null
  });

  // Filter state
  const [filterState, setFilterState] = useState({
    activeFilters: [],
    presets: config.presets || [],
    activePreset: null,
    lastUpdated: new Date()
  });

  // Custom hooks for functionality
  // Destructure stable callbacks from persistence to avoid object-identity churn
  const {
    loadFilters: loadPersistedFilters,
    saveFilters: persistSaveFilters,
    clearFilters: persistClearFilters,
    savePresets: persistSavePresets
  } = useFilterPersistence(config);
  const validation = useFilterValidation(config);
  const debouncedFilters = useFilterDebounce(filterState.activeFilters, config.debounceMs || 300);

  // Memoized filter definitions with context-specific defaults
  const filterDefinitions = useMemo(() => {
    if (config.filters && config.filters.length > 0) {
      return config.filters;
    }
    return CONTEXT_FILTER_DEFINITIONS[config.context] || [];
  }, [config.filters, config.context]);

  // Initialize module
  useEffect(() => {
    const initializeModule = async () => {
      try {
        setModuleState(prev => ({ ...prev, loading: true }));

        // Load persisted filters if enabled
        if (config.persistFilters) {
          try {
            const persistedState = await loadPersistedFilters();
            if (persistedState) {
              setFilterState(prev => ({
                ...prev,
                ...persistedState,
                lastUpdated: new Date()
              }));
            }
          } catch (persistenceError) {
            console.warn('Failed to load persisted filters, continuing with defaults:', persistenceError);
            // Continue with initialization even if persistence fails
          }
        }

        setModuleState(prev => ({ 
          ...prev, 
          initialized: true, 
          loading: false 
        }));

        // Emit module loaded event
        emitEvent({
          type: ModuleEventType.MODULE_LOADED,
          payload: {
            moduleId: 'filter-module',
            context: config.context,
            filterCount: filterDefinitions.length
          },
          source: 'filter-module',
          timestamp: Date.now()
        });

      } catch (error) {
        console.error('FilterModule initialization error:', error);
        setModuleState(prev => ({ 
          ...prev, 
          initialized: false, 
          loading: false,
          error: error.message 
        }));
      }
    };

    initializeModule();
  }, [config, loadPersistedFilters, filterDefinitions.length, emitEvent]);

  // Handle filter changes with debouncing for real-time filtering
  useEffect(() => {
    if (config.realTimeFiltering && moduleState.initialized) {
      handleFilterChange(debouncedFilters);
    }
  }, [debouncedFilters, config.realTimeFiltering, moduleState.initialized]);

  /**
   * Handle filter value changes
   */
  const handleFilterValueChange = useCallback((filterId, value, operator = null) => {
    const filterDefinition = filterDefinitions.find(f => f.id === filterId);
    if (!filterDefinition) {
      console.warn(`Filter definition not found for ID: ${filterId}`);
      return;
    }

    // Validate the new value
    const validationResult = validation.validateFilterValue(filterDefinition, value);
    if (!validationResult.valid) {
      context.toast.error(`Invalid filter value: ${validationResult.errors.join(', ')}`);
      return;
    }

    setFilterState(prev => {
      const existingFilterIndex = prev.activeFilters.findIndex(f => f.id === filterId);
      let newActiveFilters;

      if (value === null || value === undefined || value === '') {
        // Remove filter if value is empty
        if (existingFilterIndex >= 0) {
          newActiveFilters = prev.activeFilters.filter(f => f.id !== filterId);
        } else {
          newActiveFilters = prev.activeFilters;
        }
      } else {
        // Create or update filter
        const newFilter = createActiveFilter(filterDefinition, value, operator);
        
        if (existingFilterIndex >= 0) {
          newActiveFilters = [...prev.activeFilters];
          newActiveFilters[existingFilterIndex] = newFilter;
        } else {
          newActiveFilters = [...prev.activeFilters, newFilter];
        }
      }

      const newState = {
        ...prev,
        activeFilters: newActiveFilters,
        activePreset: null, // Clear active preset when manually changing filters
        lastUpdated: new Date()
      };

      // Persist filters if enabled
      if (config.persistFilters) {
        persistSaveFilters(newState);
      }

      return newState;
    });

    // Emit filter change event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'filter_value_changed',
        filterId,
        value,
        operator,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });
  }, [filterDefinitions, validation, context, config, persistSaveFilters, emitEvent]);

  /**
   * Handle filter changes (called by debounced effect or manual trigger)
   */
  const handleFilterChange = useCallback((filters) => {
    if (config.onFilterChange) {
      config.onFilterChange(filters);
    }

    // Emit filter change event
    emitEvent({
      type: ModuleEventType.MODULE_DATA_CHANGED,
      payload: {
        action: 'filters_applied',
        activeFilters: filters,
        filterCount: filters.length,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });
  }, [config, emitEvent]);

  /**
   * Reset all filters
   */
  const handleFilterReset = useCallback(() => {
    setFilterState(prev => ({
      ...prev,
      activeFilters: [],
      activePreset: null,
      lastUpdated: new Date()
    }));

    // Clear persisted filters
    if (config.persistFilters) {
      persistClearFilters();
    }

    // Call reset callback
    if (config.onFilterReset) {
      config.onFilterReset();
    }

    // Emit reset event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'filters_reset',
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });

    // Show success message
    context.toast.success('Filters cleared');
  }, [config, persistClearFilters, context, emitEvent]);

  /**
   * Apply a filter preset
   */
  const handlePresetApply = useCallback((preset) => {
    setFilterState(prev => ({
      ...prev,
      activeFilters: [...preset.filters],
      activePreset: preset.id,
      lastUpdated: new Date()
    }));

    // Persist the new state
    if (config.persistFilters) {
      persistSaveFilters({
        activeFilters: preset.filters,
        activePreset: preset.id
      });
    }

    // Emit preset applied event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'preset_applied',
        presetId: preset.id,
        presetName: preset.name,
        filterCount: preset.filters.length,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });

    context.toast.success(`Applied preset: ${preset.name}`);
  }, [config, persistSaveFilters, context, emitEvent]);

  /**
   * Save current filters as a preset
   */
  const handlePresetSave = useCallback((name, description = '') => {
    if (filterState.activeFilters.length === 0) {
      context.toast.warning('No active filters to save as preset');
      return;
    }

    const newPreset = {
      id: `preset-${Date.now()}`,
      name,
      description,
      filters: [...filterState.activeFilters],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setFilterState(prev => ({
      ...prev,
      presets: [...prev.presets, newPreset],
      activePreset: newPreset.id
    }));

    // Persist presets
    if (config.persistFilters) {
      persistSavePresets([...filterState.presets, newPreset]);
    }

    // Emit preset saved event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'preset_saved',
        presetId: newPreset.id,
        presetName: name,
        filterCount: filterState.activeFilters.length,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });

    context.toast.success(`Preset saved: ${name}`);
  }, [filterState, config, persistSavePresets, context, emitEvent]);

  /**
   * Delete a filter preset
   */
  const handlePresetDelete = useCallback((presetId) => {
    const preset = filterState.presets.find(p => p.id === presetId);
    if (!preset) return;

    setFilterState(prev => ({
      ...prev,
      presets: prev.presets.filter(p => p.id !== presetId),
      activePreset: prev.activePreset === presetId ? null : prev.activePreset
    }));

    // Persist updated presets
    if (config.persistFilters) {
      const updatedPresets = filterState.presets.filter(p => p.id !== presetId);
      persistSavePresets(updatedPresets);
    }

    // Emit preset deleted event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'preset_deleted',
        presetId,
        presetName: preset.name,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });

    context.toast.success(`Deleted preset: ${preset.name}`);
  }, [filterState, config, persistSavePresets, context, emitEvent]);

  /**
   * Export filter configuration
   */
  const handleExportFilters = useCallback(() => {
    const exportData = {
      context: config.context,
      activeFilters: filterState.activeFilters,
      presets: filterState.presets,
      exportedAt: new Date().toISOString(),
      version: '1.0.0'
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `filters-${config.context}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Emit export event
    emitEvent({
      type: ModuleEventType.USER_ACTION,
      payload: {
        action: 'filters_exported',
        filterCount: filterState.activeFilters.length,
        presetCount: filterState.presets.length,
        context: config.context
      },
      source: 'filter-module',
      timestamp: Date.now()
    });

    context.toast.success('Filters exported successfully');
  }, [config, filterState, context, emitEvent]);

  /**
   * Import filter configuration
   */
  const handleImportFilters = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        // Validate import data
        if (!importData.context || importData.context !== config.context) {
          context.toast.error('Import file context does not match current context');
          return;
        }

        // Import filters and presets
        setFilterState(prev => ({
          ...prev,
          activeFilters: importData.activeFilters || [],
          presets: [...prev.presets, ...(importData.presets || [])],
          lastUpdated: new Date()
        }));

        // Persist imported data
        if (config.persistFilters) {
          persistSaveFilters({
            activeFilters: importData.activeFilters || [],
            presets: [...filterState.presets, ...(importData.presets || [])]
          });
        }

        // Emit import event
        emitEvent({
          type: ModuleEventType.USER_ACTION,
          payload: {
            action: 'filters_imported',
            importedFilters: (importData.activeFilters || []).length,
            importedPresets: (importData.presets || []).length,
            context: config.context
          },
          source: 'filter-module',
          timestamp: Date.now()
        });

        context.toast.success('Filters imported successfully');
      } catch (error) {
        console.error('Import error:', error);
        context.toast.error('Failed to import filters: Invalid file format');
      }
    };
    reader.readAsText(file);
  }, [config, filterState, persistSaveFilters, context, emitEvent]);

  // Show error state if module failed to initialize
  if (!moduleState.initialized && moduleState.error) {
    return (
      <div className={`filter-module-error ${className}`} data-testid={`${testId}-error`}>
        <div className="error-content">
          <h3>Filter Module Error</h3>
          <p>{moduleState.error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (!moduleState.initialized || moduleState.loading) {
    return (
      <div className={`filter-module-loading ${className}`} data-testid={`${testId}-loading`}>
        <div className="loading-content">
          <div className="loading-spinner" />
          <p>Initializing filters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`filter-module ${className}`} data-testid={testId}>
      {/* Filter Summary */}
      {filterState.activeFilters.length > 0 && (
        <FilterSummary
          activeFilters={filterState.activeFilters}
          onFilterRemove={(filterId) => handleFilterValueChange(filterId, null)}
          onClearAll={config.showClearAll ? handleFilterReset : null}
          testId={`${testId}-summary`}
        />
      )}

      {/* Filter Interface */}
      <FilterInterface
        config={config}
        filterDefinitions={filterDefinitions}
        activeFilters={filterState.activeFilters}
        onFilterChange={handleFilterValueChange}
        onReset={handleFilterReset}
        loading={moduleState.loading}
        testId={`${testId}-interface`}
      />

      {/* Filter Presets */}
      {(config.presets || filterState.presets.length > 0) && (
        <FilterPresets
          presets={filterState.presets}
          activePreset={filterState.activePreset}
          onPresetApply={handlePresetApply}
          onPresetSave={handlePresetSave}
          onPresetDelete={handlePresetDelete}
          onExport={config.showExport ? handleExportFilters : null}
          onImport={config.showImport ? handleImportFilters : null}
          testId={`${testId}-presets`}
        />
      )}
    </div>
  );
};

/**
 * Get default configuration for FilterModule
 */
FilterModule.getDefaultConfig = (context) => {
  return getDefaultFilterConfig(context);
};

export default FilterModule;
